import { useCallback, useEffect, useMemo, useState } from "react";
import type React from "react";
import { AlertTriangle, Building2, ChevronDown, ChevronUp, Download, FileSpreadsheet, FileText, Layers, Link2, Plus, ShieldCheck, Trash2 } from "lucide-react";
import { theme, card, btnPrimary, type as text } from "../../theme";
import { api, apiBlob, apiErrorMessage } from "../../api/client";
import { saveBlob } from "../../lib/download";
import { LoadingBlock } from "../../components/Spinner";
import { RulerProgress } from "../../components/Drawing";
import { PortalLink } from "../../components/PortalLink";
import { RegisterToolbar, SummaryTiles } from "../../components/RegisterToolbar";
import { downloadCsv, matchesSearch, by, type SortOption } from "../../lib/registerExport";
import { DetailFields, EditButton, RecordActions } from "../../components/RecordView";
import { ModalOverlay, ModalHeader, Field, Check, EmptyState, KeyValue, input, textarea, secondaryBtn, dangerBtn, toDateInput, fromInput, fmtDateOnly } from "../resilience/shared";
import {
  TONE, DAY, Pill, TierChip, YesNo, Section, ErrorText, useSaver, AlertList, GapsChecklist, CountBadge,
  byCriticality, th, td, theadRow, DateCell, isValidLei, SUBSTITUTABILITY_OPTIONS,
  type DoraContext, type DoraThirdParty, type ThirdPartiesResponse, type RoiSummary, type Subcontractor, type Tone, type ThirdPartyField, type SyncDirection,
} from "./shared";

export type ProviderFilter = "all" | "critical" | "sla" | "review" | "attention" | "exit" | "concentrated";
export const PROVIDER_FILTERS: ProviderFilter[] = ["all", "critical", "sla", "review", "attention", "exit", "concentrated"];

interface Props {
  canEdit: boolean;
  context: DoraContext | null;
  openId: string | null;
  onOpen: (id: string | null) => void;
  filter: ProviderFilter;
  onFilter: (f: ProviderFilter) => void;
  onChanged: () => void;
}

const SLA: Record<string, { tone: Tone; label: string }> = {
  MET: { tone: "success", label: "Met" },
  BREACHED: { tone: "danger", label: "Breached" },
  UNKNOWN: { tone: "neutral", label: "Unknown" },
};

const TIER_ORDER = ["CRITICAL", "HIGH", "MEDIUM", "LOW"] as const;
const YEAR = 365 * DAY;

const reviewDue = (tp: DoraThirdParty) => !!tp.nextReviewDate && new Date(tp.nextReviewDate).getTime() <= Date.now() + 30 * DAY;
const hasExitDoc = (tp: DoraThirdParty) => !!tp.exitStrategy?.trim();
/** Art. 28(8) — mirrors exitStrategyGaps in backend/src/lib/doraMonitorRules.ts. */
const exitGap = (tp: DoraThirdParty) => {
  if (!tp.isCritical) return false;
  const hasPlan = tp.exitPlanInPlace || hasExitDoc(tp);
  if (!hasPlan) return true;
  if (tp.exitPlanInPlace && !hasExitDoc(tp)) return true;
  return !tp.exitTestedAt || Date.now() - new Date(tp.exitTestedAt).getTime() > YEAR;
};
const day = (iso: string | null) => (iso ? iso.slice(0, 10) : "");

export function DoraThirdPartiesTab({ canEdit, context, openId, onOpen, filter, onFilter, onChanged }: Props) {
  const [data, setData] = useState<ThirdPartiesResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("critical");

  const load = useCallback(async () => {
    try { setData(await api.get<ThirdPartiesResponse>("/dora/third-parties")); setError(null); }
    catch (e) { setError(apiErrorMessage(e, "Could not load the register of information")); setData({ thirdParties: [], unregisteredSuppliers: [] }); }
  }, []);
  useEffect(() => { void load(); }, [load]);
  const reload = async () => { await load(); onChanged(); };

  const selected = useMemo(() => data?.thirdParties.find((t) => t.id === openId) ?? null, [data, openId]);
  // Opening a provider shows the record; Edit swaps in the form, and a different record (or none) resets it.
  const [editing, setEditing] = useState(false);
  useEffect(() => { setEditing(false); }, [openId]);

  const sortOptions: SortOption<DoraThirdParty>[] = useMemo(() => [
    { key: "critical", label: "Critical first", compare: (a, b) => Number(b.isCritical) - Number(a.isCritical) || a.name.localeCompare(b.name) },
    { key: "name", label: "Name", compare: by.text((t) => t.name) },
    { key: "tier", label: "Risk tier", compare: by.rank((t) => t.riskTier, TIER_ORDER) },
    { key: "review", label: "Next review", compare: by.date((t) => t.nextReviewDate, "asc") },
    { key: "missing", label: "Most register gaps", compare: by.number((t) => t.roiMissing.length + t.gaps.length, "desc") },
  ], []);

  if (!data) return <LoadingBlock />;
  const all = data.thirdParties;
  const lists: Record<ProviderFilter, DoraThirdParty[]> = {
    all,
    critical: all.filter((t) => t.isCritical),
    sla: all.filter((t) => t.slaStatus === "BREACHED"),
    review: all.filter(reviewDue),
    attention: all.filter((t) => t.gaps.length + t.reconcile.length + t.comparison.length + t.alerts.length > 0),
    exit: all.filter(exitGap),
    concentrated: all.filter((t) => t.concentration?.concentrated),
  };
  const toggle = (f: ProviderFilter) => onFilter(filter === f ? "all" : f);
  const shown = lists[filter]
    .filter((t) => matchesSearch(search, t.name, t.serviceType, t.supplier?.name, t.lei, t.providerCountry, t.functionSupported, t.notes))
    .sort((sortOptions.find((o) => o.key === sort) ?? sortOptions[0]).compare);

  function exportCsv() {
    downloadCsv("dora-ict-third-parties", [
      { header: "Provider", value: (t) => t.name },
      { header: "ICT service", value: (t) => t.serviceType },
      { header: "Critical or important function", value: (t) => t.isCritical },
      { header: "Function supported", value: (t) => t.functionSupported },
      { header: "Risk tier", value: (t) => t.riskTier },
      { header: "LEI", value: (t) => t.lei },
      { header: "Country", value: (t) => t.providerCountry },
      { header: "Substitutability", value: (t) => t.substitutability },
      { header: "Data locations", value: (t) => t.dataLocations.join("; ") },
      { header: "Data sensitivity", value: (t) => t.dataSensitivity },
      { header: "Subcontractors", value: (t) => (t.subcontractors === null ? "Not answered" : t.subcontractors.map((s) => s.name).join("; ") || "None") },
      { header: "Contract in place", value: (t) => t.contractInPlace },
      { header: "Contract start", value: (t) => day(t.contractStartDate) },
      { header: "Contract end", value: (t) => day(t.contractEndDate) },
      { header: "Annual cost (EUR)", value: (t) => t.annualCostEur },
      { header: "Exit strategy documented", value: (t) => hasExitDoc(t) },
      { header: "Exit plan last tested", value: (t) => day(t.exitTestedAt) },
      { header: "Last due diligence", value: (t) => day(t.lastDueDiligence) },
      { header: "Next review", value: (t) => day(t.nextReviewDate) },
      { header: "SLA", value: (t) => t.slaStatus },
      { header: "Supplier register", value: (t) => t.supplier?.name ?? "" },
      { header: "Register of Information gaps", value: (t) => t.roiMissing.length },
    ], shown);
  }

  return (
    <div style={{ display: "grid", gap: 16 }}>
      <SummaryTiles tiles={[
        { key: "all", label: "All providers", value: all.length, active: filter === "all", onClick: () => onFilter("all") },
        { key: "critical", label: "Critical function", value: lists.critical.length, tone: "accent", active: filter === "critical", onClick: () => toggle("critical") },
        { key: "attention", label: "Needs attention", value: lists.attention.length, tone: lists.attention.length ? "warning" : "neutral", active: filter === "attention", onClick: () => toggle("attention") },
        { key: "exit", label: "Exit strategy gaps", value: lists.exit.length, hint: "Art. 28(8)", tone: lists.exit.length ? "danger" : "success", active: filter === "exit", onClick: () => toggle("exit") },
        { key: "concentrated", label: "Concentration", value: lists.concentrated.length, hint: "several critical functions", tone: lists.concentrated.length ? "warning" : "neutral", active: filter === "concentrated", onClick: () => toggle("concentrated") },
        { key: "sla", label: "SLA breached", value: lists.sla.length, tone: lists.sla.length ? "danger" : "neutral", active: filter === "sla", onClick: () => toggle("sla") },
        { key: "review", label: "Review due", value: lists.review.length, hint: "within 30 days", tone: lists.review.length ? "warning" : "neutral", active: filter === "review", onClick: () => toggle("review") },
      ]} />

      {data.registerOfInformation && <RegisterOfInformationCard roi={data.registerOfInformation} onOpen={onOpen} />}

      {canEdit && data.unregisteredSuppliers.length > 0 && <AddFromSuppliers suppliers={data.unregisteredSuppliers} onAdded={reload} />}

      <div style={{ ...text.hint, maxWidth: 820 }}>
        The register of information on ICT third-party providers (Art. 28(3)). Link each provider to the supplier register: its agreements, questionnaire scores and the assets it delivers are then read from there, and anything the two disagree on is flagged for you to reconcile.
      </div>

      <RegisterToolbar<DoraThirdParty> search={search} onSearch={setSearch} placeholder="Search providers, LEI, country, function"
        sortOptions={sortOptions} sort={sort} onSort={setSort} onExport={exportCsv} count={{ shown: shown.length, total: all.length }}>
        {filter !== "all" && <button type="button" style={secondaryBtn} onClick={() => onFilter("all")}>Clear filter</button>}
        {canEdit && <button type="button" style={btnPrimary} onClick={() => setCreating(true)}><Plus size={14} style={{ verticalAlign: -2, marginRight: 6 }} />Add provider</button>}
      </RegisterToolbar>
      {error && <div style={{ ...card, ...text.body, padding: 12, color: theme.color.dangerText }}>{error}</div>}

      {shown.length === 0 ? (
        <div style={card}>
          <EmptyState title={filter === "all" && !search ? "No ICT providers registered" : "Nothing here"} detail="Every ICT third-party provider belongs in the register of information, with its contract, due diligence, exit strategy and review date. Add them from your supplier register above, or one at a time." action={canEdit && filter === "all" && !search ? <button type="button" style={btnPrimary} onClick={() => setCreating(true)}>Add a provider</button> : undefined} />
        </div>
      ) : (
        <div style={{ ...card, padding: 0, overflowX: "auto" }}>
          <table style={{ ...text.body, width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={theadRow}>{["Provider", "Critical function", "Tier", "Contract", "Exit plan", "Due diligence", "SLA", "Next review", "Supplier register", "To do"].map((h) => <th key={h} style={th}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {shown.map((tp) => {
                const sla = SLA[tp.slaStatus] ?? { tone: "neutral" as Tone, label: tp.slaStatus };
                const breaches = tp.alerts.filter((a) => a.severity === "breach").length;
                const todo = tp.gaps.length + tp.reconcile.length + tp.comparison.length;
                return (
                  <tr key={tp.id} onClick={() => onOpen(tp.id)} style={{ cursor: "pointer", borderBottom: "1px solid var(--c-border)" }}>
                    <td style={{ ...td, minWidth: 180 }}>
                      <div style={{ ...text.itemTitle, display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                        {tp.name}
                        {tp.concentration?.concentrated && <Pill tone="warning" title={tp.concentration.reason ?? undefined}>Concentration</Pill>}
                      </div>
                      <div style={{ ...text.hint, marginTop: 2 }}>{tp.serviceType}{tp.providerCountry ? ` · ${tp.providerCountry}` : ""}</div>
                    </td>
                    <td style={td}>{tp.isCritical ? <Pill tone="danger">Critical</Pill> : <span style={{ color: theme.color.faint }}>—</span>}</td>
                    <td style={td}><TierChip tier={tp.riskTier} /></td>
                    <td style={td}><YesNo value={tp.contractInPlace} title={tp.contractInPlace ? "Contract in place" : "No contract recorded"} /></td>
                    <td style={td}>
                      {!tp.isCritical
                        ? (tp.exitPlanInPlace || hasExitDoc(tp) ? <YesNo value title="Exit strategy in place" /> : <span style={{ color: theme.color.faint }} title="No exit strategy recorded">—</span>)
                        : exitGap(tp)
                          ? <span title="Art. 28(8): a documented exit strategy, tested in the last 12 months, is required for a critical function" style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><YesNo value={false} /><span style={{ ...text.meta, color: TONE.danger.color }}>{!tp.exitPlanInPlace && !hasExitDoc(tp) ? "None" : !hasExitDoc(tp) ? "Not written" : "Not tested"}</span></span>
                          : <YesNo value title={`Documented; tested ${fmtDateOnly(tp.exitTestedAt)}`} />}
                    </td>
                    <td style={{ ...td, whiteSpace: "nowrap" }}>{tp.lastDueDiligence ? <span style={{ color: theme.color.muted }}><YesNo value title="Due diligence recorded" /> <span style={text.hint}>{fmtDateOnly(tp.lastDueDiligence)}</span></span> : <YesNo value={false} title="No due diligence recorded" />}</td>
                    <td style={td}><Pill tone={sla.tone}>{sla.label}</Pill></td>
                    <td style={td}><DateCell iso={tp.nextReviewDate} warnDays={30} /></td>
                    <td style={{ ...td, whiteSpace: "nowrap" }} onClick={(e) => e.stopPropagation()}>
                      {tp.supplier
                        ? <PortalLink to={`/tprm?supplier=${tp.supplier.id}`} style={{ ...text.hint, color: "var(--c-accentText)" }}>{tp.supplier.name}</PortalLink>
                        : <span style={{ ...text.hint, color: TONE.warning.color }}>Not linked</span>}
                    </td>
                    <td style={{ ...td, whiteSpace: "nowrap" }}>
                      <span style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
                        <CountBadge n={breaches} tone="danger" title="Breaches" />
                        <CountBadge n={todo} tone="warning" title={[...tp.comparison.map((c) => `${c.label}: not as in the supplier register`), ...tp.reconcile, ...tp.gaps].join("\n")} />
                        <CountBadge n={tp.roiMissing.length} tone="neutral" title={`Register of Information: ${tp.roiMissing.length} field(s) missing`} />
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {creating && (
        <ProviderModal key="new" tp={null} context={context}
          onClose={() => setCreating(false)}
          onSaved={async (id) => { setCreating(false); await reload(); onOpen(id); }}
          onDeleted={async () => { setCreating(false); }} />
      )}
      {!creating && selected && editing && canEdit && (
        <ProviderModal key={`edit-${selected.id}`} tp={selected} context={context}
          onClose={() => setEditing(false)}
          onSaved={async () => { await reload(); setEditing(false); }}
          onDeleted={async () => { setEditing(false); onOpen(null); await reload(); }} />
      )}
      {!creating && selected && !(editing && canEdit) && (
        <ProviderView key={`view-${selected.id}`} tp={selected} canEdit={canEdit}
          onClose={() => onOpen(null)} onEdit={() => setEditing(true)} reload={reload} />
      )}
    </div>
  );
}

// ── Register of Information card ──────────────────────────────────────────────

function RegisterOfInformationCard({ roi, onOpen }: { roi: RoiSummary; onOpen: (id: string | null) => void }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pctTone = (p: number): Tone => (p === 100 ? "success" : p >= 60 ? "warning" : "danger");

  async function download() {
    setBusy(true); setError(null);
    try {
      const blob = await apiBlob("/dora/register-of-information.xlsx");
      saveBlob(blob, `DORA Register of Information ${new Date().toISOString().slice(0, 10)}.xlsx`);
    } catch (e) { setError(apiErrorMessage(e, "Could not download the register")); }
    finally { setBusy(false); }
  }

  const byProvider = new Map<string, { name: string; id: string | null; items: RoiSummary["missing"] }>();
  for (const m of roi.missing) {
    const k = m.providerId ?? "__entity";
    const entry = byProvider.get(k) ?? { name: m.providerName ?? "Your organisation", id: m.providerId, items: [] };
    entry.items.push(m);
    byProvider.set(k, entry);
  }

  return (
    <div style={{ ...card, padding: 0 }}>
      <div style={{ padding: "14px 18px", display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", borderBottom: "1px solid var(--c-border)" }}>
        <FileSpreadsheet size={16} style={{ color: "var(--c-accentText)" }} />
        <div style={{ flex: 1, minWidth: 220 }}>
          <div style={{ ...text.sectionTitle, margin: 0 }}>Register of Information</div>
          <div style={{ ...text.hint, marginTop: 2 }}>
            The Art. 28(3) register in the templates of Implementing Regulation (EU) 2024/2956, built from the providers below and their agreements in the supplier register. A blank is never assumed: it is listed here as missing.
          </div>
        </div>
        <Pill tone={pctTone(roi.completeness.percent)}>{roi.completeness.percent}% complete</Pill>
        <button type="button" style={btnPrimary} disabled={busy} onClick={download}><Download size={13} style={{ verticalAlign: -2, marginRight: 6 }} />{busy ? "Preparing…" : "Download workbook"}</button>
      </div>
      <div style={{ padding: "12px 18px", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 10 }}>
        {roi.templates.map((t) => {
          const tone = TONE[pctTone(t.completeness.percent)];
          return (
            <div key={t.code} title={`${t.completeness.filled} of ${t.completeness.required} required fields answered`}>
              <div style={{ ...text.hint, display: "flex", justifyContent: "space-between", gap: 6 }}>
                <span style={{ color: theme.color.text }}><strong>{t.code}</strong> {t.title}</span>
                <span style={{ color: tone.color, fontWeight: 600 }}>{t.completeness.percent}%</span>
              </div>
              <RulerProgress value={t.completeness.percent} fill={tone.border} label={`${t.code} completeness`} height={10} style={{ marginTop: 5 }} />
              <div style={{ ...text.meta, marginTop: 3 }}>{t.rows} row{t.rows === 1 ? "" : "s"}</div>
            </div>
          );
        })}
      </div>
      {roi.concentrated.length > 0 && (
        <div style={{ margin: "0 18px 12px", padding: "10px 12px", borderRadius: 3, background: TONE.warning.bg, border: `1px solid ${TONE.warning.border}` }}>
          <div style={{ ...text.label, color: TONE.warning.color, display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}><Layers size={14} />Concentration risk</div>
          {roi.concentrated.map((c) => (
            <button key={c.providerId} type="button" onClick={() => onOpen(c.providerId)} style={{ all: "unset", cursor: "pointer", ...text.body, display: "block", marginTop: 2 }}>
              {c.reason}{c.incidents > 0 ? ` ${c.incidents} ICT incident${c.incidents === 1 ? "" : "s"} came through it.` : ""}
            </button>
          ))}
        </div>
      )}
      {roi.missing.length > 0 && (
        <div style={{ borderTop: "1px solid var(--c-border)", padding: "10px 18px" }}>
          <button type="button" onClick={() => setOpen((v) => !v)} style={{ all: "unset", cursor: "pointer", ...text.hint, display: "inline-flex", alignItems: "center", gap: 4 }}>
            {open ? <ChevronUp size={13} /> : <ChevronDown size={13} />}Missing data ({roi.missing.length})
          </button>
          {open && (
            <div style={{ display: "grid", gap: 10, marginTop: 10, maxHeight: 340, overflowY: "auto" }}>
              {[...byProvider.values()].map((g) => (
                <div key={g.id ?? "entity"}>
                  {g.id
                    ? <button type="button" onClick={() => onOpen(g.id)} style={{ all: "unset", cursor: "pointer", ...text.itemTitle, color: "var(--c-accentText)" }}>{g.name}</button>
                    : <span style={text.itemTitle}>{g.name}</span>}
                  <ul style={{ margin: "4px 0 0", paddingLeft: 18, display: "grid", gap: 2 }}>
                    {g.items.map((m, idx) => <li key={idx} style={text.hint}><strong style={{ color: theme.color.text }}>{m.template}</strong> {m.message}</li>)}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {error && <div style={{ padding: "0 18px 12px" }}><ErrorText error={error} /></div>}
    </div>
  );
}

function AddFromSuppliers({ suppliers, onAdded }: { suppliers: ThirdPartiesResponse["unregisteredSuppliers"]; onAdded: () => Promise<void> }) {
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState<string[]>([]);
  const saver = useSaver();
  const allOn = picked.length === suppliers.length;

  async function add() {
    if (picked.length === 0) return;
    const ok = await saver.run(async () => { await api.post("/dora/third-parties/from-suppliers", { supplierIds: picked }); await onAdded(); }, "Could not add the suppliers");
    if (ok) setPicked([]);
  }

  return (
    <div style={{ ...card, padding: 0 }}>
      <button type="button" onClick={() => setOpen((v) => !v)} style={{ all: "unset", cursor: "pointer", display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", width: "calc(100% - 32px)" }}>
        <Building2 size={16} style={{ color: "var(--c-accentText)" }} />
        <span style={{ ...text.body, flex: 1 }}>
          <strong>{suppliers.length} supplier{suppliers.length === 1 ? "" : "s"}</strong> in your supplier register {suppliers.length === 1 ? "is" : "are"} not in the DORA register yet.
          <span style={{ color: theme.color.muted }}> Add the ICT ones in one go — name, service and tier are taken from the supplier register.</span>
        </span>
        {open ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
      </button>
      {open && (
        <div style={{ borderTop: "1px solid var(--c-border)", padding: 16, display: "grid", gap: 10 }}>
          <label style={{ ...text.hint, display: "flex", gap: 8, alignItems: "center", cursor: "pointer" }}>
            <input type="checkbox" checked={allOn} onChange={() => setPicked(allOn ? [] : suppliers.map((s) => s.id))} /> Select all
          </label>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 6, maxHeight: 260, overflowY: "auto" }}>
            {suppliers.map((s) => {
              const on = picked.includes(s.id);
              return (
                <label key={s.id} style={{ display: "flex", gap: 8, alignItems: "center", padding: "7px 10px", borderRadius: 3, border: `1px solid ${on ? theme.color.accent : "var(--c-border)"}`, background: on ? "var(--c-accentBg)" : "transparent", cursor: "pointer", ...text.body }}>
                  <input type="checkbox" checked={on} onChange={() => setPicked((p) => (on ? p.filter((x) => x !== s.id) : [...p, s.id]))} />
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ color: theme.color.text, fontWeight: 500 }}>{s.name}</span>
                    {s.category && <span style={{ ...text.meta, display: "block" }}>{s.category}</span>}
                  </span>
                  <TierChip tier={s.riskTier} />
                </label>
              );
            })}
          </div>
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <button type="button" style={btnPrimary} disabled={saver.busy || picked.length === 0} onClick={add}>{saver.busy ? "Adding…" : `Add ${picked.length || ""} to the register`.replace("  ", " ")}</button>
            <span style={text.meta}>A supplier delivering a critical or essential asset is marked as supporting a critical function.</span>
          </div>
          <ErrorText error={saver.error} />
        </div>
      )}
    </div>
  );
}

/**
 * A reconcile prompt the page can settle with one boolean, or null when it needs a person. Fields the
 * two registers both hold (contract in place, dates, tier) are the comparison table instead.
 */
function reconcileFix(text: string): { label: string; body: Record<string, boolean> } | null {
  if (/critical or important function\?/i.test(text)) return { label: "Mark as supporting a critical function", body: { isCritical: true } };
  return null;
}

const DATE_FIELDS = new Set<ThirdPartyField>(["contractStartDate", "contractEndDate", "lastDueDiligence", "nextReviewDate"]);
function comparisonValue(field: ThirdPartyField, v: string | boolean | null): React.ReactNode {
  if (v === null) return <span style={{ color: theme.color.faint }}>Not recorded</span>;
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (DATE_FIELDS.has(field)) return fmtDateOnly(v);
  if (field === "riskTier" || field === "slaStatus") return /^[A-Z_]+$/.test(v) ? v.charAt(0) + v.slice(1).toLowerCase().replace(/_/g, " ") : v;
  return v;
}

/**
 * Where this row and the linked supplier register now differ, field by field. Each row is synced
 * only on an explicit click, in the direction the server allows; a row without a sync is a check.
 */
function SupplierComparison({ tp, canEdit, reload }: { tp: DoraThirdParty; canEdit: boolean; reload: () => Promise<void> }) {
  const saver = useSaver();
  const fromSupplier = tp.comparison.filter((c) => c.sync === "from_supplier" || c.sync === "either").map((c) => c.field);
  async function sync(direction: SyncDirection, fields: ThirdPartyField[]) {
    await saver.run(async () => { await api.post(`/dora/third-parties/${tp.id}/sync`, { direction, fields }); await reload(); }, "Could not sync with the supplier register");
  }
  return (
    <div style={{ padding: "12px 14px", borderRadius: 3, border: `1px solid ${TONE.warning.border}` }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap", marginBottom: 8 }}>
        <div style={{ ...text.label, color: TONE.warning.color, display: "flex", alignItems: "center", gap: 6 }}><AlertTriangle size={14} />Compared with the supplier register</div>
        {canEdit && fromSupplier.length > 1 && (
          <button type="button" style={secondaryBtn} disabled={saver.busy} onClick={() => sync("from_supplier", fromSupplier)}>Take all from supplier register</button>
        )}
      </div>
      <div style={{ overflowX: "auto" }}>
        <table style={{ ...text.body, width: "100%", borderCollapse: "collapse" }}>
          <thead><tr style={theadRow}>{["Field", "DORA register", "Supplier register", ...(canEdit ? [""] : [])].map((h, i) => <th key={i} style={th}>{h}</th>)}</tr></thead>
          <tbody>
            {tp.comparison.map((c) => (
              <tr key={c.field} style={{ borderBottom: "1px solid var(--c-border)" }}>
                <td style={td}>
                  <div style={{ fontWeight: 600 }}>{c.label}</div>
                  {c.note && <div style={{ ...text.hint, marginTop: 2 }}>{c.note}</div>}
                </td>
                <td style={td}>{comparisonValue(c.field, c.dora)}</td>
                <td style={td}>{comparisonValue(c.field, c.supplier)}</td>
                {canEdit && (
                  <td style={{ ...td, whiteSpace: "nowrap" }}>
                    <span style={{ display: "inline-flex", gap: 6, flexWrap: "wrap" }}>
                      {(c.sync === "from_supplier" || c.sync === "either") && <button type="button" style={secondaryBtn} disabled={saver.busy} onClick={() => sync("from_supplier", [c.field])}>Take from supplier register</button>}
                      {(c.sync === "to_supplier" || c.sync === "either") && <button type="button" style={secondaryBtn} disabled={saver.busy} onClick={() => sync("to_supplier", [c.field])}>Update supplier register</button>}
                      {c.sync === null && <span style={text.hint}>Check by hand</span>}
                    </span>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <ErrorText error={saver.error} />
    </div>
  );
}

interface SubRow { name: string; lei: string; country: string; service: string }

const SUBSTITUTABILITY_LABEL = (v: string | null) => (v ? SUBSTITUTABILITY_OPTIONS.find((o) => o.id === v)?.label ?? v : null);
const yesNoText = (v: boolean) => (v ? "Yes" : "No");

/** Opening a provider: the read-only record, with Edit and the one-click reconcile fixes (Actions) for those who may make them. */
function ProviderView({ tp, canEdit, onClose, onEdit, reload }: {
  tp: DoraThirdParty; canEdit: boolean; onClose: () => void; onEdit: () => void; reload: () => Promise<void>;
}) {
  const quick = useSaver();
  const s = tp.supplierSummary;
  const importantAssets = s ? s.assets.filter((a) => a.criticality === "critical" || a.isEssentialFunction).length : 0;
  const conc = tp.concentration;
  const sla = SLA[tp.slaStatus] ?? { tone: "neutral" as Tone, label: tp.slaStatus };
  const fixes = tp.reconcile.map((r) => reconcileFix(r)).filter((x): x is NonNullable<ReturnType<typeof reconcileFix>> => !!x);

  async function apply(body: Record<string, boolean>) {
    await quick.run(async () => { await api.put(`/dora/third-parties/${tp.id}`, body); await reload(); }, "Could not apply the change");
  }

  return (
    <ModalOverlay onClose={onClose} wide>
      <ModalHeader
        title={<span style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>{tp.name}{tp.isCritical && <Pill tone="danger">Critical function</Pill>}<TierChip tier={tp.riskTier} /></span>}
        onClose={onClose}
        right={canEdit ? <EditButton onClick={onEdit} /> : undefined}
      />
      <div style={{ padding: 20, display: "grid", gap: 18 }}>
        <AlertList alerts={tp.alerts} />

        {tp.reconcile.length > 0 && (
          <div style={{ padding: "12px 14px", borderRadius: 3, background: TONE.warning.bg, border: `1px solid ${TONE.warning.border}` }}>
            <div style={{ ...text.label, color: TONE.warning.color, display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}><AlertTriangle size={14} />The supplier register says otherwise</div>
            <ul style={{ margin: 0, paddingLeft: 18, display: "grid", gap: 4 }}>
              {tp.reconcile.map((r) => <li key={r} style={text.body}>{r}</li>)}
            </ul>
          </div>
        )}
        {tp.comparison.length > 0 && <SupplierComparison tp={tp} canEdit={canEdit} reload={reload} />}
        <GapsChecklist gaps={tp.gaps} />

        {conc && (conc.criticalFunctions > 0 || conc.criticalAssets > 0 || conc.incidents > 0) && (
          <div style={{ padding: "10px 12px", borderRadius: 3, background: conc.concentrated ? TONE.warning.bg : theme.color.panelAlt, border: `1px solid ${conc.concentrated ? TONE.warning.border : "var(--c-border)"}` }}>
            <div style={{ ...text.label, marginBottom: 0, color: conc.concentrated ? TONE.warning.color : theme.color.text, display: "flex", alignItems: "center", gap: 6 }}><Layers size={14} />What depends on this provider</div>
            <div style={{ ...text.body, marginTop: 4 }}>
              {conc.criticalFunctions} critical or important function{conc.criticalFunctions === 1 ? "" : "s"}{conc.functions.length ? ` (${conc.functions.join(", ")})` : ""} · {conc.criticalAssets} critical or essential asset{conc.criticalAssets === 1 ? "" : "s"} · {conc.incidents} ICT incident{conc.incidents === 1 ? "" : "s"} through it
            </div>
            {conc.reason && <div style={{ ...text.hint, color: TONE.warning.color, marginTop: 4 }}>{conc.reason}</div>}
          </div>
        )}

        <Section title="Provider" icon={<FileText size={15} />}>
          <DetailFields fields={[
            { label: "Name", value: tp.name },
            { label: "ICT service provided", value: tp.serviceType },
            { label: "Linked supplier", value: tp.supplier ? <PortalLink to={`/tprm?supplier=${tp.supplier.id}`} style={{ color: "var(--c-accentText)" }}>{tp.supplier.name}</PortalLink> : <span style={{ color: TONE.warning.color }}>Not linked</span> },
            { label: "Risk tier", value: <TierChip tier={tp.riskTier} /> },
            { label: "SLA performance", value: <Pill tone={sla.tone}>{sla.label}</Pill> },
            { label: "Supports a critical or important function", value: yesNoText(tp.isCritical) },
            { label: "Contract in place", value: yesNoText(tp.contractInPlace) },
            { label: "Exit strategy in place", value: yesNoText(tp.exitPlanInPlace) },
            { label: "Audit right exercised", value: yesNoText(tp.auditRightExercised) },
            { label: "Last due diligence", value: tp.lastDueDiligence ? fmtDateOnly(tp.lastDueDiligence) : null },
            { label: "Next review", value: tp.nextReviewDate ? <DateCell iso={tp.nextReviewDate} warnDays={30} /> : null },
            { label: "Notes", value: tp.notes, wide: true, multiline: true },
          ]} />
        </Section>

        <Section title="Register of Information" icon={<FileSpreadsheet size={15} />}
          help="The fields the Art. 28(3) register asks for (Implementing Regulation (EU) 2024/2956). A blank is listed as missing, never assumed.">
          {tp.roiMissing.length > 0 && (
            <div style={{ ...text.hint, color: TONE.warning.color, background: TONE.warning.bg, borderRadius: 3, padding: "8px 12px", marginBottom: 12 }}>
              Missing for the register: {tp.roiMissing.map((m) => `${m.template} ${m.message}`).join(" ")}
            </div>
          )}
          <DetailFields fields={[
            { label: "LEI", value: tp.lei ? <span style={{ fontVariantNumeric: text.ref.fontVariantNumeric }}>{tp.lei}</span> : null },
            { label: "Country of head office", value: tp.providerCountry },
            { label: "Function(s) supported", value: tp.functionSupported, wide: true, multiline: true },
            { label: "Substitutability", value: SUBSTITUTABILITY_LABEL(tp.substitutability) },
            { label: "Data sensitivity", value: tp.dataSensitivity ? tp.dataSensitivity.charAt(0).toUpperCase() + tp.dataSensitivity.slice(1) : null },
            { label: "Data locations", value: tp.dataLocations.join(", "), wide: true },
            { label: "Contract start", value: tp.contractStartDate ? fmtDateOnly(tp.contractStartDate) : null },
            { label: "Contract end", value: tp.contractEndDate ? fmtDateOnly(tp.contractEndDate) : null },
            { label: "Annual cost (EUR)", value: tp.annualCostEur != null ? tp.annualCostEur.toLocaleString("en-GB") : null },
            { label: "Exit plan last tested", value: tp.exitTestedAt ? fmtDateOnly(tp.exitTestedAt) : null },
            { label: "Exit strategy", value: tp.exitStrategy, wide: true, multiline: true },
          ]} />
          <div style={{ marginTop: 14 }}>
            <div style={{ ...text.label, marginBottom: 6 }}>ICT subcontractors (supply chain)</div>
            {tp.subcontractors === null ? (
              <div style={{ ...text.body, color: TONE.warning.color }}>Not answered.</div>
            ) : tp.subcontractors.length === 0 ? (
              <div style={text.body}>This provider uses no ICT subcontractors for the service.</div>
            ) : (
              <table style={{ ...text.body, width: "100%", borderCollapse: "collapse" }}>
                <thead><tr style={theadRow}>{["Name", "LEI", "Country", "Service"].map((h) => <th key={h} style={th}>{h}</th>)}</tr></thead>
                <tbody>
                  {tp.subcontractors.map((sc, idx) => (
                    <tr key={idx} style={{ borderBottom: "1px solid var(--c-border)" }}>
                      <td style={td}>{sc.name}</td>
                      <td style={{ ...td, fontVariantNumeric: text.ref.fontVariantNumeric }}>{sc.lei || "—"}</td>
                      <td style={td}>{sc.country || "—"}</td>
                      <td style={td}>{sc.service || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </Section>

        <Section title="In the supplier register" icon={<Link2 size={15} />}
          help={s ? "Read live from the supplier register, so the two never drift apart." : "Not linked. Edit the provider and pick the supplier to see its agreements, questionnaire score and the assets it delivers."}
          right={tp.supplier ? <PortalLink to={`/tprm?supplier=${tp.supplier.id}`} style={{ ...text.hint, color: "var(--c-accentText)" }}>Open {tp.supplier.name}</PortalLink> : undefined}>
          {s && (
            <div style={{ display: "grid", gap: 14 }}>
              <KeyValue items={[
                ["Agreements in force", <span key="c" style={{ color: s.contractsInForce > 0 ? TONE.success.color : TONE.danger.color, fontWeight: 600 }}>{s.contractsInForce} of {s.contracts}</span>],
                ["Latest questionnaire score", s.latestScore != null ? String(s.latestScore) : <span key="q" style={{ color: TONE.warning.color }}>No scored questionnaire</span>],
                ["Register tier", <TierChip key="t" tier={s.registerTier} />],
                ["Register status", s.status.replace(/_/g, " ")],
              ]} />
              <div>
                <div style={{ ...text.label, marginBottom: 6, display: "flex", alignItems: "center", gap: 6 }}>
                  <ShieldCheck size={14} />Assets it delivers ({s.assets.length}){importantAssets > 0 && <span style={{ ...text.hint, fontWeight: 400, color: TONE.danger.color }}>· {importantAssets} critical or essential</span>}
                </div>
                {s.assets.length === 0 ? <div style={text.hint}>None linked in the asset register.</div> : (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {[...s.assets].sort(byCriticality).map((a) => {
                      const imp = a.criticality === "critical" || a.isEssentialFunction;
                      return (
                        <PortalLink key={a.id} to={`/resilience/assets?asset=${a.id}`} style={{ textDecoration: "none" }}>
                          <span style={{ ...text.hint, padding: "3px 10px", borderRadius: 2, display: "inline-block", border: `1px solid ${imp ? TONE.danger.border : "var(--c-border)"}`, background: imp ? TONE.danger.bg : theme.color.panelAlt, color: imp ? TONE.danger.color : theme.color.text }}>
                            {a.name} · {a.criticality}{a.isEssentialFunction ? " · essential" : ""}
                          </span>
                        </PortalLink>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}
        </Section>

        {canEdit && fixes.length > 0 && (
          <RecordActions note="Settle what the supplier register disagrees on in one click; anything else is changed through Edit.">
            {fixes.map((fix) => <button key={fix.label} type="button" style={secondaryBtn} disabled={quick.busy} onClick={() => apply(fix.body)}>{fix.label}</button>)}
          </RecordActions>
        )}
        <ErrorText error={quick.error} />
      </div>
    </ModalOverlay>
  );
}

/** The existing provider form: adding a provider, or Edit from the record view (Save returns to the view). */
function ProviderModal({ tp, context, onClose, onSaved, onDeleted }: {
  tp: DoraThirdParty | null; context: DoraContext | null;
  onClose: () => void; onSaved: (id: string) => Promise<void>; onDeleted: () => Promise<void>;
}) {
  const [f, setF] = useState({
    name: tp?.name ?? "", serviceType: tp?.serviceType ?? "", riskTier: tp?.riskTier ?? "LOW", slaStatus: tp?.slaStatus ?? "UNKNOWN",
    isCritical: tp?.isCritical ?? false, contractInPlace: tp?.contractInPlace ?? false, exitPlanInPlace: tp?.exitPlanInPlace ?? false, auditRightExercised: tp?.auditRightExercised ?? false,
    lastDueDiligence: toDateInput(tp?.lastDueDiligence ?? null), nextReviewDate: toDateInput(tp?.nextReviewDate ?? null), notes: tp?.notes ?? "", supplierId: tp?.supplierId ?? "",
    // Register of Information
    lei: tp?.lei ?? "", providerCountry: tp?.providerCountry ?? "", functionSupported: tp?.functionSupported ?? "",
    substitutability: tp?.substitutability ?? "", dataLocations: (tp?.dataLocations ?? []).join(", "), dataSensitivity: tp?.dataSensitivity ?? "",
    contractStartDate: toDateInput(tp?.contractStartDate ?? null), contractEndDate: toDateInput(tp?.contractEndDate ?? null),
    annualCostEur: tp?.annualCostEur != null ? String(tp.annualCostEur) : "", exitStrategy: tp?.exitStrategy ?? "", exitTestedAt: toDateInput(tp?.exitTestedAt ?? null),
  });
  const [subs, setSubs] = useState<SubRow[]>((tp?.subcontractors ?? []).map((s: Subcontractor) => ({ name: s.name, lei: s.lei ?? "", country: s.country ?? "", service: s.service ?? "" })));
  /** "No ICT subcontractors" is an answer; leaving the list empty without it is not. */
  const [noSubs, setNoSubs] = useState(tp?.subcontractors !== null && tp?.subcontractors !== undefined && tp.subcontractors.length === 0);
  const saver = useSaver();
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF((p) => ({ ...p, [k]: v }));

  function pickSupplier(id: string) {
    const s = context?.suppliers.find((x) => x.id === id);
    setF((p) => ({
      ...p, supplierId: id,
      ...(s && !p.name.trim() ? { name: s.name } : {}),
      ...(s && !p.serviceType.trim() && s.category ? { serviceType: s.category } : {}),
    }));
  }

  const leiError = f.lei.trim() && !isValidLei(f.lei) ? "Not a valid LEI: 20 characters, the last two check digits (ISO 17442)." : null;
  const subLeiError = subs.find((s) => s.lei.trim() && !isValidLei(s.lei));

  async function save() {
    if (!f.name.trim() || !f.serviceType.trim()) { saver.setError("Name and service are both needed"); return; }
    if (leiError) { saver.setError(leiError); return; }
    if (subLeiError) { saver.setError(`Subcontractor ${subLeiError.name || "row"}: the LEI is not valid.`); return; }
    const cost = f.annualCostEur.trim() === "" ? null : Number(f.annualCostEur);
    if (cost !== null && (!Number.isFinite(cost) || cost < 0)) { saver.setError("Annual cost must be a positive number"); return; }
    const cleanSubs = subs.filter((s) => s.name.trim()).map((s) => ({ name: s.name.trim(), lei: s.lei.trim() || null, country: s.country.trim() || null, service: s.service.trim() }));
    const body = {
      name: f.name.trim(), serviceType: f.serviceType.trim(), riskTier: f.riskTier, slaStatus: f.slaStatus,
      isCritical: f.isCritical, contractInPlace: f.contractInPlace, exitPlanInPlace: f.exitPlanInPlace, auditRightExercised: f.auditRightExercised,
      lastDueDiligence: fromInput(f.lastDueDiligence), nextReviewDate: fromInput(f.nextReviewDate), notes: f.notes.trim() || null, supplierId: f.supplierId || null,
      lei: f.lei.trim() || null, providerCountry: f.providerCountry.trim() || null, functionSupported: f.functionSupported.trim() || null,
      substitutability: f.substitutability || null, dataSensitivity: f.dataSensitivity || null,
      dataLocations: f.dataLocations.split(/[,;\n]+/).map((x) => x.trim()).filter(Boolean),
      subcontractors: cleanSubs.length > 0 ? cleanSubs : noSubs ? [] : null,
      contractStartDate: fromInput(f.contractStartDate), contractEndDate: fromInput(f.contractEndDate), annualCostEur: cost,
      exitStrategy: f.exitStrategy.trim() || null, exitTestedAt: fromInput(f.exitTestedAt),
    };
    await saver.run(async () => {
      const r = tp ? await api.put<{ thirdParty: DoraThirdParty }>(`/dora/third-parties/${tp.id}`, body) : await api.post<{ thirdParty: DoraThirdParty }>("/dora/third-parties", body);
      await onSaved(r.thirdParty.id);
    }, "Could not save the provider");
  }

  async function remove() {
    if (!tp || !window.confirm(`Remove ${tp.name} from the DORA register of information?`)) return;
    await saver.run(async () => { await api.del(`/dora/third-parties/${tp.id}`); await onDeleted(); }, "Could not delete the provider");
  }

  const s = tp?.supplierSummary ?? null;

  return (
    <ModalOverlay onClose={onClose} wide>
      <ModalHeader
        title={tp ? `Edit ${tp.name}` : "Add an ICT provider"}
        onClose={onClose}
        right={tp ? <button type="button" style={dangerBtn} disabled={saver.busy} onClick={remove}>Delete</button> : undefined}
      />
      <div style={{ padding: 20, display: "grid", gap: 18 }}>
        <Section title="Provider" icon={<FileText size={15} />}>
          <fieldset style={{ border: "none", padding: 0, margin: 0, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12 }}>
            <Field label="Linked supplier" span2 help="Link the supplier register record so agreements, assessments and delivered assets are read from there.">
              <select style={input} value={f.supplierId} onChange={(e) => pickSupplier(e.target.value)}>
                <option value="">Not linked</option>
                {(context?.suppliers ?? []).map((x) => <option key={x.id} value={x.id}>{x.name}{x.category ? ` — ${x.category}` : ""}</option>)}
              </select>
            </Field>
            <Field label="Name"><input style={input} value={f.name} onChange={(e) => set("name", e.target.value)} maxLength={200} /></Field>
            <Field label="ICT service provided"><input style={input} value={f.serviceType} onChange={(e) => set("serviceType", e.target.value)} maxLength={200} placeholder="e.g. Cloud hosting" /></Field>
            <Field label="Risk tier">
              <select style={input} value={f.riskTier} onChange={(e) => set("riskTier", e.target.value)}>
                {["LOW", "MEDIUM", "HIGH", "CRITICAL"].map((t) => <option key={t} value={t}>{t.charAt(0) + t.slice(1).toLowerCase()}</option>)}
              </select>
            </Field>
            <Field label="SLA performance">
              <select style={input} value={f.slaStatus} onChange={(e) => set("slaStatus", e.target.value)}>
                {Object.entries(SLA).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </Field>
            <Field label="Last due diligence" help="Art. 28(4): before the contract and at renewal."><input type="date" style={input} value={f.lastDueDiligence} onChange={(e) => set("lastDueDiligence", e.target.value)} /></Field>
            <Field label="Next review"><input type="date" style={input} value={f.nextReviewDate} onChange={(e) => set("nextReviewDate", e.target.value)} /></Field>
            <div style={{ gridColumn: "1 / -1", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 10 }}>
              <Check checked={f.isCritical} onChange={(v) => set("isCritical", v)} label="Supports a critical or important function" help="Needs an exit strategy and stricter contract terms (Art. 28(8), Art. 30(3))." />
              <Check checked={f.contractInPlace} onChange={(v) => set("contractInPlace", v)} label="Contract in place" help="Holding the Art. 30 key provisions." />
              <Check checked={f.exitPlanInPlace} onChange={(v) => set("exitPlanInPlace", v)} label="Exit strategy in place" help="Write it down below, and record when it was last tested (Art. 28(8))." />
              <Check checked={f.auditRightExercised} onChange={(v) => set("auditRightExercised", v)} label="Audit right exercised" help="Access, inspection and audit rights used (Art. 30(3)(e))." />
            </div>
            <Field label="Notes" span2><textarea style={textarea} rows={2} value={f.notes} onChange={(e) => set("notes", e.target.value)} /></Field>
          </fieldset>
        </Section>

        <Section title="Register of Information" icon={<FileSpreadsheet size={15} />}
          help="The fields the Art. 28(3) register asks for (Implementing Regulation (EU) 2024/2956). Leave a field blank if you do not know: it is then listed as missing, never assumed.">
          {tp && tp.roiMissing.length > 0 && (
            <div style={{ ...text.hint, color: TONE.warning.color, background: TONE.warning.bg, borderRadius: 3, padding: "8px 12px", marginBottom: 12 }}>
              Missing for the register: {tp.roiMissing.map((m) => `${m.template} ${m.message}`).join(" ")}
            </div>
          )}
          <fieldset style={{ border: "none", padding: 0, margin: 0, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12 }}>
            <Field label="LEI" help={leiError ?? "Legal Entity Identifier, 20 characters (ISO 17442)."}>
              <input style={{ ...input, borderColor: leiError ? theme.color.danger : undefined, fontVariantNumeric: text.ref.fontVariantNumeric }} value={f.lei} onChange={(e) => set("lei", e.target.value.toUpperCase())} maxLength={20} placeholder="e.g. 5493001KJTIIGC8Y1R12" />
            </Field>
            <Field label="Country of head office"><input style={input} value={f.providerCountry} onChange={(e) => set("providerCountry", e.target.value)} maxLength={100} placeholder="e.g. Ireland" /></Field>
            <Field label="Function(s) supported" span2 help="The business function the service supports; one per line or separated by semicolons.">
              <textarea style={textarea} rows={2} value={f.functionSupported} onChange={(e) => set("functionSupported", e.target.value)} maxLength={2000} placeholder="e.g. Payments processing; Customer onboarding" />
            </Field>
            <Field label="Substitutability">
              <select style={input} value={f.substitutability} onChange={(e) => set("substitutability", e.target.value)}>
                <option value="">Not assessed</option>
                {SUBSTITUTABILITY_OPTIONS.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
              </select>
            </Field>
            <Field label="Data sensitivity">
              <select style={input} value={f.dataSensitivity} onChange={(e) => set("dataSensitivity", e.target.value)}>
                <option value="">Not recorded</option>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </Field>
            <Field label="Data locations" span2 help="Countries or regions where the data is stored or processed, separated by commas.">
              <input style={input} value={f.dataLocations} onChange={(e) => set("dataLocations", e.target.value)} placeholder="e.g. Ireland, Netherlands" />
            </Field>
            <Field label="Contract start" help={s ? "Agreements in the supplier register are used for the register when there are any." : undefined}><input type="date" style={input} value={f.contractStartDate} onChange={(e) => set("contractStartDate", e.target.value)} /></Field>
            <Field label="Contract end"><input type="date" style={input} value={f.contractEndDate} onChange={(e) => set("contractEndDate", e.target.value)} /></Field>
            <Field label="Annual cost (EUR)"><input type="number" min={0} step="any" style={input} value={f.annualCostEur} onChange={(e) => set("annualCostEur", e.target.value)} /></Field>
            <Field label="Exit plan last tested" help="Art. 28(8): test at least every 12 months."><input type="date" style={input} value={f.exitTestedAt} onChange={(e) => set("exitTestedAt", e.target.value)} /></Field>
            <Field label="Exit strategy" span2 help="How the service would be moved to another provider or brought back in house, and how long it would take.">
              <textarea style={textarea} rows={3} value={f.exitStrategy} onChange={(e) => set("exitStrategy", e.target.value)} maxLength={10000} />
            </Field>
          </fieldset>

          <div style={{ marginTop: 14 }}>
            <div style={{ ...text.label, marginBottom: 6 }}>ICT subcontractors (supply chain)</div>
            {subs.length === 0 && (
              <label style={{ ...text.hint, display: "flex", gap: 8, alignItems: "center", cursor: "pointer", marginBottom: 8 }}>
                <input type="checkbox" checked={noSubs} onChange={(e) => setNoSubs(e.target.checked)} /> This provider uses no ICT subcontractors for the service
              </label>
            )}
            {subs.length > 0 && (
              <div style={{ display: "grid", gap: 6, marginBottom: 8 }}>
                {subs.map((row, idx) => {
                  const bad = row.lei.trim() && !isValidLei(row.lei);
                  const upd = (k: keyof SubRow, v: string) => setSubs((p) => p.map((x, j) => (j === idx ? { ...x, [k]: v } : x)));
                  return (
                    <div key={idx} style={{ display: "grid", gridTemplateColumns: "minmax(120px, 2fr) minmax(150px, 2fr) minmax(90px, 1fr) minmax(120px, 2fr) auto", gap: 6, alignItems: "center" }}>
                      <input style={input} value={row.name} onChange={(e) => upd("name", e.target.value)} placeholder="Name" maxLength={200} aria-label="Subcontractor name" />
                      <input style={{ ...input, fontVariantNumeric: text.ref.fontVariantNumeric, borderColor: bad ? theme.color.danger : undefined }} value={row.lei} onChange={(e) => upd("lei", e.target.value.toUpperCase())} placeholder="LEI" maxLength={20} aria-label="Subcontractor LEI" title={bad ? "Not a valid LEI" : undefined} />
                      <input style={input} value={row.country} onChange={(e) => upd("country", e.target.value)} placeholder="Country" maxLength={100} aria-label="Subcontractor country" />
                      <input style={input} value={row.service} onChange={(e) => upd("service", e.target.value)} placeholder="Service" maxLength={300} aria-label="Service subcontracted" />
                      <button type="button" style={{ ...secondaryBtn, padding: "6px 8px" }} onClick={() => setSubs((p) => p.filter((_, j) => j !== idx))} aria-label="Remove subcontractor"><Trash2 size={13} /></button>
                    </div>
                  );
                })}
              </div>
            )}
            <button type="button" style={secondaryBtn} onClick={() => { setSubs((p) => [...p, { name: "", lei: "", country: "", service: "" }]); setNoSubs(false); }}><Plus size={12} style={{ verticalAlign: -2, marginRight: 4 }} />Add subcontractor</button>
          </div>

          <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 16 }}>
            <button type="button" style={btnPrimary} disabled={saver.busy} onClick={save}>{saver.busy ? "Saving…" : tp ? "Save provider" : "Add provider"}</button>
            <button type="button" style={secondaryBtn} onClick={onClose}>Cancel</button>
          </div>
          <ErrorText error={saver.error} />
        </Section>
      </div>
    </ModalOverlay>
  );
}
